import { SpcSamplingMethod } from "src/components/views/spc/masterData/controlCharts/form/samplingMethod/SpcSamplingMethod.tsx";
import i18n from "i18next";
import { ControlChartFormRenderProps } from "src/components/views/spc/masterData/controlCharts/form/ControlChartForm.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { ControlChartCommonApi } from "src/api/generated/spc/controlChart/api/controlChartCommonApi.ts";
import { Skeleton } from "@mui/material";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { ControlChartSelectionForm } from "src/components/views/spc/controlChart/dataInput/ControlChartSelectionForm.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { ControlChartView } from "src/api/generated/postgres/db/types/public_/tables/controlChartView.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export const otherControlChartsSumOverSamplingPeriodSamplingMethod = (): SpcSamplingMethod => {
	return {
		key: "other_control_charts_sum_over_sampling_period",
		label: i18n.t("control_charts_sum_over_sampling_period"),
		renderParameterFields: (props: ControlChartFormRenderProps) => {
			return <ParameterFields {...props} />;
		},
	};
};

// eslint-disable-next-line react-refresh/only-export-components
const ParameterFields = (props: ControlChartFormRenderProps) => {
	const { watch } = props;

	const controlChartIdsJson = watch("samplingParams.param1");
	const controlChartIds: number[] = controlChartIdsJson == null ? [] : JSON.parse(controlChartIdsJson);

	return (
		<AsyncFetchRender
			noContainer
			fetch={async () => {
				return await ControlChartCommonApi.getControlCharts({
					controlChartIds,
				});
			}}
			skeleton={
				<Skeleton
					variant="rounded"
					sx={{
						alignSelf: "stretch",
						width: 100,
						height: "initial",
					}}
				/>
			}
			content={(controlCharts) => <AsyncContent initialControlCharts={controlCharts} {...props} />}
		/>
	);
};

interface AsyncContentProps extends ControlChartFormRenderProps {
	initialControlCharts: ControlChartView[];
}

// eslint-disable-next-line react-refresh/only-export-components
const AsyncContent = ({ initialControlCharts, setValue }: AsyncContentProps) => {
	const { openDialog } = useGenericDialog();

	const [controlCharts, setControlCharts] = useState<ControlChartView[]>(initialControlCharts);

	return (
		<VerticalBox
			sx={{
				gridColumn: "1 / -1",
				gap: 1,
				padding: 1,
				border: "1px solid",
				borderRadius: 1,
				borderColor: "divider",
			}}
		>
			<Typography variant={"h6"}>{i18n.t("control_charts_to_sum")}</Typography>
			{controlCharts.map((controlChart) => (
				<HorizontalBox key={controlChart.chartId} sx={{ alignItems: "center", gap: 0.5 }}>
					<AsyncButton
						icon={faMinus}
						size={"small"}
						tooltip={i18n.t("remove")}
						onClick={() => {
							setControlChartsWrapped(
								controlCharts.filter((chart) => chart.chartId !== controlChart.chartId),
							);
						}}
					/>
					<Typography>{controlChart.chartName}</Typography>
				</HorizontalBox>
			))}
			<AsyncButton
				icon={faPlus}
				label={i18n.t("add")}
				sx={{
					alignSelf: "flex-start",
				}}
				onClick={() =>
					openDialog(({ closeDialog }) => ({
						title: i18n.t("select_control_chart"),
						size: "sm",
						agentContext: {
							key: defineAgentUiContextKey("spc.samplingMethod.addControlChart.dialog"),
							pathSegments: [i18n.t("select_control_chart")],
						},
						content: (
							<ControlChartSelectionForm
								currentControlChartId={undefined}
								onSubmit={async (controlChart) => {
									if (controlChart == null) return;
									if (controlCharts.some((chart) => chart.chartId === controlChart.chartId)) return;

									setControlChartsWrapped([...controlCharts, controlChart]);
								}}
								closeDialog={closeDialog}
							/>
						),
					}))
				}
			/>
		</VerticalBox>
	);

	function setControlChartsWrapped(newControlCharts: ControlChartView[]) {
		setControlCharts(newControlCharts);
		const newControlChartIds = newControlCharts.map((chart) => chart.chartId);
		setValue("samplingParams.param1", JSON.stringify(newControlChartIds));
	}
};
